import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, LogIn, AlertCircle, ArrowUpFromDotIcon } from 'lucide-react';
import { sanitizeEmail, sanitizeText, detectXSSPatterns } from '../../utils/security';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Sanitize and validate inputs
      const sanitizedEmail = sanitizeEmail(formData.email);
      const sanitizedPassword = sanitizeText(formData.password);

      // Check for XSS patterns
      if (detectXSSPatterns(formData.email) || detectXSSPatterns(formData.password)) {
        setError('Entrada inválida detectada. Por favor, não use caracteres especiais.');
        setIsLoading(false);
        return;
      }

      // Validate email format
      if (!sanitizedEmail) {
        setError('Por favor, insira um endereço de email válido.');
        setIsLoading(false);
        return;
      }

      // Validate password is not empty
      if (!sanitizedPassword || sanitizedPassword.length < 6) {
        setError('A password deve ter pelo menos 6 caracteres.');
        setIsLoading(false);
        return;
      }

      await login(sanitizedEmail, sanitizedPassword);
      navigate(from, { replace: true });
    } catch (err: any) {
      // Don't expose backend error messages that might leak system information
      const errorMessage = err.response?.status === 401 
        ? 'Email ou password incorretos.' 
        : 'Erro ao fazer login. Por favor, tente novamente.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-black rounded flex items-center justify-center">
           <img src="logo512.png" alt="Logo" className="h-10 w-10" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-secondary-900">
            ABIDE Restaurantes
          </h2>
          <p className="mt-2 text-sm text-secondary-600">
            Faça login na sua conta
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field"
                placeholder="seu.email@exemplo.com"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="input-field pr-10"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-secondary-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-secondary-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary flex justify-center items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="spinner" />
                  <span>A entrar...</span>
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  <span>Entrar</span>
                </>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default LoginForm;
